import { useMemo, useCallback } from "react"
import { useParams } from "react-router-dom"
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  List,
  Typography,
  useTheme,
  Divider,
  Stack,
  Button,
} from "@mui/material"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import { size } from "lodash"

import { getDurationDate } from "utils/date"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"
import { useEventDetail } from "hooks/event/useEventDetail"
import { useMobileUrl } from "hooks/useMobileUrl"
import { useImageModal } from "hooks/imageModal/useImageModal"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"

import { LocationIcon } from "components/icons"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { Head } from "components/Head"
import { InfoListItem } from "components/ListItem"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { QRCode } from "components/QRCode"
import { LanguageSelectMenu } from "components/Language/LanguageSelectMenu"
import { Carousel, CarouselItem } from "components/Carousel"
import { ImageCarouselModal } from "components/Image"

export const EventDetailKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const params = useParams()
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()
  const { eventId } = params
  const { t } = useTranslation()
  const { event, navigateToLocation } = useEventDetail(eventId)
  const { goBack, openSearch } = useAppUI()
  const { mobileUrl } = useMobileUrl()

  const theme = useTheme()

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayIcon />,
    [theme]
  )

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    card,
    infoListItem,
    cardContent,
    cardHeader,
    cardContentPrimaryText,
    cardContentSecondaryText,
    navigationWrapper,
    divider,
    infoContainer,
    qrCodeContainer,
    qrCode,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    buttonContainer,
    button,
    cardContentTextWrapper,
    cardMediaWrapper,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    toolbarContainer,
    actionButton,
    actionContainer,
    searchBarContainer,
    languageSelectMenu,
  } = styleOverrides
  const { galleryCarouselOption } =
    theme.components[`EventDetailPage`]?.defaultProps || {}

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const allImagesUrls = useMemo(
    () =>
      size(event?.allImages) > 0 ? event?.allImages?.map((v) => v?.url) : [],
    [event?.allImages]
  )

  if (!event) return null

  return (
    <>
      <Head title={`${t(event.name)} (${t("Event Detail")})`} />
      <Layout>
        {!event ? null : (
          <Card elevation={0} sx={card}>
            <Box sx={cardMediaWrapper}>
              {event.allImages && (
                <Box sx={galleryCarouselWrapper}>
                  <Carousel
                    options={{
                      type: "fade",
                      padding: 0,
                      gap: 8,
                      pagination: true,
                      autoplay: true,
                      ...galleryCarouselOption,
                    }}
                    sx={galleryCarousel}
                  >
                    {event.allImages.map(({ id, url }) => {
                      return (
                        <CarouselItem
                          key={`slide-${id}`}
                          sx={galleryCarouselItem}
                        >
                          <CardMedia
                            component="img"
                            sx={galleryCarouselItem}
                            src={url}
                            onClick={() => handleImage(url)}
                          />
                        </CarouselItem>
                      )
                    })}
                  </Carousel>
                </Box>
              )}
            </Box>
            <CardHeader title={t(event.name)} sx={cardHeader} />
            <Divider sx={divider} />
            <List sx={infoContainer}>
              <InfoListItem
                sx={infoListItem}
                icon={calendarIcon}
                primary={getDurationDate(event.startDate, event.endDate)}
                secondary={event.openingText}
              />
              {event.hasLocation && (
                <InfoListItem
                  sx={infoListItem}
                  icon={locationIcon}
                  primary={
                    <LocationInfo feature={event.feature} component="span" />
                  }
                  secondary={t(event.boothName)}
                />
              )}
            </List>
            {mobileUrl && (
              <Box sx={qrCodeContainer}>
                <QRCode sx={qrCode} url={mobileUrl} />
                <Stack>
                  <Typography
                    variant="subtitle2"
                    component="p"
                    sx={qrcodePrimaryText}
                  >
                    {t("Scan Here")}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    sx={qrcodeSecondaryText}
                  >
                    {t("For Mobile")}
                  </Typography>
                </Stack>
              </Box>
            )}
            <Divider sx={divider} />
            <CardContent sx={cardContent}>
              <Box sx={cardContentTextWrapper}>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={cardContentPrimaryText}
                >
                  {t("Event Detail")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={cardContentSecondaryText}
                  dangerouslySetInnerHTML={{
                    __html: event.descriptionText,
                  }}
                ></Typography>
              </Box>
            </CardContent>
            <Box sx={buttonContainer}>
              <Button variant="outlined" sx={button} onClick={handleClickBack}>
                {t("Back")}
              </Button>
              {event.hasLocation && (
                <Button
                  variant="contained"
                  sx={button}
                  onClick={navigateToLocation}
                >
                  {t("View Location")}
                </Button>
              )}
            </Box>
          </Card>
        )}
        <Box key="app-navigation" sx={navigationWrapper}>
          <Box sx={toolbarContainer}>
            <Box sx={actionContainer}>
              <LanguageSelectMenu sx={languageSelectMenu} />
              <LanguageButton variant="contained" sx={actionButton} />
              {/* //TODO:: Add a wheel chair's mode handler function later */}
              {/*  <Button
            variant="contained"
            sx={actionButton}
            onClick={() => {}}
            startIcon={<RestroomWheelchairIcon />}
          /> */}
              {additionalMenu}
              {/* //TODO:: Create Toolbar component and refactor Code */}
            </Box>
            <Box sx={searchBarContainer}>
              <SearchToggleButton
                placeholder={t("Search shops and more...")}
                onClick={openSearch}
              />
            </Box>
          </Box>
          {AppNavigation && <AppNavigation />}
        </Box>
      </Layout>
      <ImageCarouselModal
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={allImagesUrls}
        onClose={handleClose}
      />
    </>
  )
}
